// @optimyl/ui — the busy indicator. Shared because both apps had one and they disagreed on size steps,
// border width, and whether it had an accessible name at all.
//
// Colour stays per-app through `ringClass`; the sizing, the spin and the announcement do not.

export interface SpinnerProps {
  size?: "sm" | "md" | "lg";
  /** Full-screen scrim. */
  overlay?: boolean;
  /**
   * Border colours, e.g. "border-brand-green border-t-transparent". Named for what it sets: it REPLACES
   * the default ring colours rather than appending, so a layout class passed here would leave the spinner
   * a uniform circle that visibly does not turn.
   */
  ringClass?: string;
  /**
   * What is loading, announced to screen readers. Pass null when an ancestor live region already announces
   * the wait — two announcements for one spinner is worse than one.
   */
  label?: string | null;
}

const sizeClasses = {
  sm: "h-4 w-4 border-2",
  md: "h-8 w-8 border-3",
  lg: "h-12 w-12 border-4",
} as const;

export function Spinner({
  size = "md",
  overlay = false,
  ringClass = "border-slate-300 border-t-slate-600",
  label = "Loading",
}: SpinnerProps) {
  const circle = (
    <div className={`${sizeClasses[size]} animate-spin rounded-full ${ringClass}`} aria-hidden="true" />
  );

  // The label is rendered as TEXT inside the live region, not as aria-label on it. A role="status" with no
  // content is an empty live region: screen readers announce a region's content changing, and an attribute
  // is not content, so the attribute form announced nothing at all.
  const spinner = label ? (
    <div role="status">
      {circle}
      <span className="sr-only">{label}</span>
    </div>
  ) : (
    circle
  );

  if (!overlay) return spinner;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70">{spinner}</div>
  );
}
